/**
 * Module-scoped last-known selected video device per room.
 *
 * The broker destroys and recreates LiveKitImpl on reconnect, and the React
 * selection sync only re-pushes after the new instance is registered — several
 * governor ticks too late. A governor booted without a selection treats the
 * operator's selected tile as an ordinary automatic pause target. LiveKitImpl
 * seeds from this registry at construction, so entries must OUTLIVE instance
 * destruction — never clear this from the instance teardown path.
 * Per rule: think-in-systems (ai.knowledge/global) — mirrors viewport-tracker's
 * room-scoped module state.
 */

// Branded device ids end-to-end so livekit.ts can seed the governor without casts.
import type { Device } from '@studio/models/device';
import type { Room } from '@studio/models/room';

const roomSelection = new Map<string, Device['id'] | null>();

/** Record a room's selected video device; null records an explicit no-selection. */
export function setSelectedDevice(roomId: string, deviceId: Device['id'] | null): void {
  roomSelection.set(roomId, deviceId);
}

/** Last selection recorded for the room; null when none was ever recorded. */
export function getSelectedDevice(roomId: Room['id']): Device['id'] | null {
  return roomSelection.get(roomId) ?? null;
}

/** Reset shared state between unit tests. */
export function _resetForTest(): void {
  roomSelection.clear();
}
